import { inject, Injector, runInInjectionContext } from '@angular/core';
import { AppActions } from '@app-shared/store/app/app.actions';
import { selectCodeApprenant, selectIdentite } from '@app-shared/store/idt/idt.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { getStructureSelected } from '@pcscol-portail/ng-authz';
import { DemandesPiecesService } from '@pieces-api';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { piecesJustificativesActions } from './pieces-justificatives.actions';
import { HttpErrorResponse } from '@angular/common/http';

function getApprenantNotFoundError(error: HttpErrorResponse, codeApprenant: string) {
  if (error?.status === 403) {
    return {
      ...error,
      message:
        error?.error?.message ??
        `L'apprenant avec le code apprenant ${codeApprenant} n'existe pas, n'existe plus ou a été archivé.`,
    };
  }
  return error;
}

type PreviewAcceptHeader = 'image/png' | 'image/jpg' | 'application/pdf';

/**
 * Load pieces justificatives
 */
export const loadPieces$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), injector = inject(Injector)) =>
    actions$.pipe(
      ofType(piecesJustificativesActions.loadPieces),
      switchMap(() =>
        runInInjectionContext(injector, () => {
          const demandesPiecesService = inject(DemandesPiecesService);

          const structureCode = store.selectSignal(getStructureSelected)()?.code;
          const codeApprenant = store.selectSignal(selectCodeApprenant)();
          const identite = store.selectSignal(selectIdentite)();
          const idApprenant = identite?.id;

          if (!structureCode || !codeApprenant || !idApprenant) {
            return of(
              piecesJustificativesActions.loadPiecesFailure({
                error: 'Structure code, code apprenant or id apprenant is missing',
              }),
            );
          }

          return demandesPiecesService.recupererDemandesPiecesParAnnee().pipe(
            map((response) =>
              piecesJustificativesActions.loadPiecesSuccess({
                pieces: response?.depotsPiecesElementAnnee ?? null,
                reutilisables: response?.depotsPiecesReutilisables ?? null,
              }),
            ),
            catchError((error) =>
              of(
                piecesJustificativesActions.loadPiecesFailure({
                  error: getApprenantNotFoundError(error, codeApprenant),
                }),
              ),
            ),
          );
        }),
      ),
    ),
  { functional: true },
);

/**
 * Download piece
 */
export const downloadPiece$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), injector = inject(Injector)) =>
    actions$.pipe(
      ofType(piecesJustificativesActions.downloadPiece),
      switchMap((action) =>
        runInInjectionContext(injector, () => {
          const demandesPiecesService = inject(DemandesPiecesService);
          const codeApprenant = store.selectSignal(selectCodeApprenant)();

          return demandesPiecesService.telechargerFichier(action.request.idDemandePiece).pipe(
            map((blob) =>
              piecesJustificativesActions.downloadPieceSuccess({
                fileName: action.request.fileName,
                originalFileName: action.request.originalFileName,
                blob,
              }),
            ),
            catchError((error) =>
              of(
                piecesJustificativesActions.downloadPieceFailure({
                  error: getApprenantNotFoundError(error, codeApprenant ?? ''),
                }),
              ),
            ),
          );
        }),
      ),
    ),
  { functional: true },
);

/**
 * Trigger file download in browser after successful download
 */
export const downloadPieceSuccess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(piecesJustificativesActions.downloadPieceSuccess),
      tap((action) => {
        const extension = action.originalFileName?.split('.').pop() || 'pdf';
        const fileName = `${action.fileName}.${extension}`;

        const url = window.URL.createObjectURL(action.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(url);
      }),
    ),
  { functional: true, dispatch: false },
);

/**
 * Upload piece (deposit or replace)
 */
export const uploadPiece$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), injector = inject(Injector)) =>
    actions$.pipe(
      ofType(piecesJustificativesActions.uploadPiece),
      switchMap((action) =>
        runInInjectionContext(injector, () => {
          const demandesPiecesService = inject(DemandesPiecesService);
          const codeApprenant = store.selectSignal(selectCodeApprenant)();

          return demandesPiecesService
            .televerserFichier(
              action.request.idDemandePiece,
              action.request.sourceDepot,
              action.request.file,
            )
            .pipe(
              switchMap(() => [
                piecesJustificativesActions.uploadPieceSuccess(),
                piecesJustificativesActions.loadPieces(),
              ]),
              catchError((error) =>
                of(
                  piecesJustificativesActions.uploadPieceFailure({
                    error: getApprenantNotFoundError(error, codeApprenant ?? ''),
                  }),
                ),
              ),
            );
        }),
      ),
    ),
  { functional: true },
);

/**
 * Update concern status
 */
export const updateConcernStatus$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), injector = inject(Injector)) =>
    actions$.pipe(
      ofType(piecesJustificativesActions.updateConcernStatus),
      switchMap((action) =>
        runInInjectionContext(injector, () => {
          const demandesPiecesService = inject(DemandesPiecesService);
          const codeApprenant = store.selectSignal(selectCodeApprenant)();

          const idDemandePiece = action.request.idDemandePiece;

          const apiCall = action.request.isConcerned
            ? demandesPiecesService.definirNonDepose(idDemandePiece)
            : demandesPiecesService.definirNonConcerne(idDemandePiece);

          return apiCall.pipe(
            switchMap(() => [
              piecesJustificativesActions.updateConcernStatusSuccess(),
              piecesJustificativesActions.loadPieces(),
            ]),
            catchError((error) =>
              of(
                piecesJustificativesActions.updateConcernStatusFailure({
                  error: getApprenantNotFoundError(error, codeApprenant ?? ''),
                }),
              ),
            ),
          );
        }),
      ),
    ),
  { functional: true },
);

/**
 * Reuse a previously deposited piece
 */
export const reusePiece$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), injector = inject(Injector)) =>
    actions$.pipe(
      ofType(piecesJustificativesActions.reusePiece),
      switchMap((action) =>
        runInInjectionContext(injector, () => {
          const demandesPiecesService = inject(DemandesPiecesService);
          const codeApprenant = store.selectSignal(selectCodeApprenant)();

          return demandesPiecesService.reutiliserDepotPiece(action.request.idDepot).pipe(
            switchMap(() => [
              piecesJustificativesActions.reusePieceSuccess(),
              piecesJustificativesActions.loadPieces(),
            ]),
            catchError((error) =>
              of(
                piecesJustificativesActions.reusePieceFailure({
                  error: getApprenantNotFoundError(error, codeApprenant ?? ''),
                }),
              ),
            ),
          );
        }),
      ),
    ),
  { functional: true },
);

/**
 * Preview a reusable deposit (fetch its blob to open in a new tab)
 */
export const previewReusable$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), injector = inject(Injector)) =>
    actions$.pipe(
      ofType(piecesJustificativesActions.previewReusable),
      switchMap((action) =>
        runInInjectionContext(injector, () => {
          const demandesPiecesService = inject(DemandesPiecesService);
          const codeApprenant = store.selectSignal(selectCodeApprenant)();

          return demandesPiecesService
            .telechargerDepot(action.request.idDepot, 'body', false, {
              httpHeaderAccept: action.request.typeMime as PreviewAcceptHeader,
            })
            .pipe(
              map((blob) =>
                piecesJustificativesActions.previewReusableSuccess({
                  blob,
                  nomFichier: action.request.nomFichier,
                  typeMime: action.request.typeMime,
                }),
              ),
              catchError((error) =>
                of(
                  piecesJustificativesActions.previewReusableFailure({
                    error: getApprenantNotFoundError(error, codeApprenant ?? ''),
                  }),
                ),
              ),
            );
        }),
      ),
    ),
  { functional: true },
);

/**
 * Open the reusable preview blob in a new browser tab
 */
export const previewReusableSuccess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(piecesJustificativesActions.previewReusableSuccess),
      tap((action) => {
        const url = window.URL.createObjectURL(action.blob);
        window.open(url, '_blank');
        // Give the new tab time to read the blob before revoking
        setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      }),
    ),
  { functional: true, dispatch: false },
);

/**
 * Display error message on failure
 */
export const displayError$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        piecesJustificativesActions.loadPiecesFailure,
        piecesJustificativesActions.downloadPieceFailure,
        piecesJustificativesActions.uploadPieceFailure,
        piecesJustificativesActions.updateConcernStatusFailure,
        piecesJustificativesActions.reusePieceFailure,
        piecesJustificativesActions.previewReusableFailure,
      ),
      map((action) => AppActions.displayError({ payload: action.error })),
    ),
  { functional: true },
);

/**
 * Display success message after successful reuse
 */
export const displayReuseSuccess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(piecesJustificativesActions.reusePieceSuccess),
      map(() =>
        AppActions.displaySuccessMessage({
          payload: 'La pièce précédemment déposée a été réutilisée avec succès',
        }),
      ),
    ),
  { functional: true },
);

/**
 * Display success message after successful upload
 */
export const displayUploadSuccess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(piecesJustificativesActions.uploadPieceSuccess),
      map(() =>
        AppActions.displaySuccessMessage({
          payload: 'La pièce justificative a été téléversée avec succès',
        }),
      ),
    ),
  { functional: true },
);

/**
 * Display success message after successful concern status update
 */
export const displayUpdateConcernSuccess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(piecesJustificativesActions.updateConcernStatusSuccess),
      map(() =>
        AppActions.displaySuccessMessage({
          payload: 'Le statut de concerné a été mis à jour avec succès',
        }),
      ),
    ),
  { functional: true },
);
